import React, { useCallback } from 'react';

import { fetchDetalheDoPedido } from '../../../api/comprasDashboardApi';
import { formatarDataPura } from '../formatarData';
import ModalDeDetalhe, { useDetalhePaginado } from './ModalDeDetalhe';
import ComentariosDeCompra from '../../../components/comentarios/ComentariosDeCompra';

/**
 * UM pedido de compra: o que as listas deixaram de mostrar, mais os itens (at-110).
 *
 * Pedido do Victor, 21/09/2026 — as tabelas de pedido tinham seis colunas e ficaram em quatro. O
 * que saiu (situação, previsão de entrega, operação) e o que nunca estava lá (a **origem**:
 * requisição, comprador, placa, oficina) vivem aqui.
 *
 * 🔴 **Os itens podem não estar no espelho, e a tela DIZ isso** (`itensNaoSincronizados`). A carga
 * histórica de itens de pedido é um disparo manual de ~3 h (at-109), e medido em 21/09/2026 o
 * espelho de dev tem 9.566 pedidos e **zero** itens. "Este pedido não tem itens" seria uma
 * afirmação sobre a compra; o que existe é ausência de sincronização, e são coisas diferentes.
 *
 * at-122 — o item traz `produto` como **UUID** do ERP, e o backend o traduz em código e descrição
 * pelo cadastro de produtos do Nasajon. Aqui o valor **existe**, ao contrário do item de requisição
 * (onde ele é zero em 100% das linhas, porque o preço nasce justamente no pedido).
 */

interface Props {
  pedidoId: string;
  /** O número do ERP, que a lista já tem — para o título aparecer antes da carga. */
  numero: string | null;
  onFechar: () => void;
}

const COLUNAS = ['Código', 'Produto', 'Qtd.', 'Valor unitário', 'Total'];

/** O que a célula diz quando o produto não tem descrição — os dois casos NÃO se confundem. */
const PRODUTO_SEM_DESCRICAO: Record<string, string> = {
  PENDENTE: 'produto ainda não identificado',
  NAO_ENCONTRADO: 'produto não encontrado no cadastro do Nasajon',
};

const MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const ROTULO_PREVISAO: Record<string, string> = {
  AGUARDANDO: 'aguardando a entrega',
  PREVISAO_VENCIDA: 'o prazo registrado já passou',
  SEM_PREVISAO: 'sem prazo registrado',
};

/** Um dado do cabeçalho. Ausente é DITO, nunca some — a lacuna é informação. */
function Dado({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  return (
    <div className="dcr-dado">
      <span className="dcr-dado-rotulo">{rotulo}</span>
      <span className="dcr-dado-valor" title={valor ?? undefined}>
        {valor ?? <span className="dcr-ausente">não informado</span>}
      </span>
    </div>
  );
}

export default function DetalheDoPedidoModal({ pedidoId, numero, onFechar }: Props) {
  const carregar = useCallback(() => fetchDetalheDoPedido(pedidoId), [pedidoId]);
  const { dados, carregando, erro } = useDetalhePaginado(carregar);

  const itens = dados?.itens ?? [];
  const semItens = itens.length === 0;

  return (
    <ModalDeDetalhe
      titulo={`Pedido ${numero ?? ''}`.trim()}
      legenda={
        carregando
          ? 'Carregando...'
          : erro
            ? 'Não foi possível carregar'
            : `${MOEDA.format(dados?.total ?? 0)} · ${dados?.situacao ?? 'situação não informada'}`
      }
      carregando={carregando}
      erro={erro}
      erroTexto={
        'Não foi possível carregar este pedido. Isto não significa que ele esteja sem dados — ' +
        'tente de novo e, se persistir, acione a TI.'
      }
      vazio={semItens}
      /*
       * 🔴 Duas frases DIFERENTES para o mesmo espaço vazio, e é a distinção que importa: uma fala
       * do pedido, a outra do espelho. Colapsá-las faria a tela afirmar que a compra não tem peças
       * quando o que falta é a sincronização.
       */
      vazioTexto={
        dados?.itensNaoSincronizados
          ? 'Os itens dos pedidos ainda não foram sincronizados do Nasajon — esta lista aparece depois que a carga de itens rodar. Isto não quer dizer que o pedido esteja vazio.'
          : 'Este pedido não tem itens no espelho do Nasajon.'
      }
      colunas={COLUNAS}
      classeTabela="dcr-tabela-itens-pedido"
      antesDaTabela={
        dados ? (
          <div className="dcr-dados">
            {/*
              🔴 Emissão e previsão do PEDIDO são datas puras (medido em produção, 06/10/2026: 0 de
              10.058 com hora). `formatarInstante` as mostrava um dia antes no fuso da oficina.
            */}
            <Dado rotulo="Emissão" valor={formatarDataPura(dados.emissao)} />
            <Dado rotulo="Fornecedor" valor={dados.fornecedorNome} />
            <Dado rotulo="Situação" valor={dados.situacao} />
            <Dado rotulo="Valor" valor={MOEDA.format(dados.total)} />
            {/*
              ⚠️ A previsão vem com o estado ao lado, e o rótulo NUNCA afirma atraso: o prazo
              registrado pode ter passado e a peça ter chegado direto na oficina externa, sem
              registro nenhum no ERP (`BUG-009` a `BUG-012`).
            */}
            <Dado
              rotulo="Previsão de entrega"
              valor={
                dados.previsaoDeEntrega
                  ? `${formatarDataPura(dados.previsaoDeEntrega)} — ${ROTULO_PREVISAO[dados.situacaoPrevisao] ?? ''}`.trim()
                  : ROTULO_PREVISAO[dados.situacaoPrevisao] ?? null
              }
            />
            <Dado rotulo="Operação" valor={dados.operacao} />
            {/*
              A origem herdada da requisição (at-109). Pedido sem origem é compra direta, ~4 em
              100 — e a célula diz isso, em vez de traço, que seria lido como dado faltando.
            */}
            <Dado rotulo="Requisição de origem" valor={dados.requisicaoCodigo} />
            <Dado rotulo="Comprador" valor={dados.compradorNome} />
            <Dado rotulo="Placa" valor={dados.placa} />
            <Dado rotulo="Oficina" valor={dados.oficina} />
            {dados.numeroExterno ? (
              /*
                ⚠️ Rótulo próprio, e não "oficina": é apelido livre digitado pelo comprador —
                medido na at-109, 313 de 522 parecem o nome do fornecedor. Chamá-lo de outra coisa
                faria alguém usá-lo como identificador.
              */
              <Dado rotulo="Apelido no ERP" valor={dados.numeroExterno} />
            ) : null}
          </div>
        ) : null
      }
      rodape={
        <span>
          {itens.length} {itens.length === 1 ? 'item' : 'itens'} · soma{' '}
          {MOEDA.format(itens.reduce((s, i) => s + (i.total ?? 0), 0))}
        </span>
      }
      // at-129: a conversa da equipe sobre o pedido (e a da requisição de origem, marcada).
      depoisDaTabela={<ComentariosDeCompra tipo="pedido" id={pedidoId} />}
      // Abre sempre SOBRE outra superfície (um modal de lista ou o card da aba Pedidos).
      empilhado
      onFechar={onFechar}
    >
      {itens.map((i) => (
        <div key={i.id} className="dcr-linha">
          <div className="dcr-celula" title={i.produtoCodigo ?? ''}>
            {i.produtoCodigo ?? <span className="dcr-ausente">-</span>}
          </div>
          <div className="dcr-celula" title={i.produtoDescricao ?? i.produto ?? ''}>
            {/*
              🔴 Sem descrição, a célula diz POR QUÊ — "ainda não identificado" (a sincronização
              não chegou nele) é diferente de "não encontrado" (o Nasajon não o devolveu). O UUID
              cru, que o Victor recusou em 11/09/2026, fica só no `title`.
            */}
            {i.produtoDescricao ?? (
              <span className="dcr-ausente">
                {i.produto ? PRODUTO_SEM_DESCRICAO[i.produtoSituacao] ?? 'sem descrição' : 'sem produto'}
              </span>
            )}
          </div>
          <div className="dcr-celula">{i.quantidade ?? '-'}</div>
          <div className="dcr-celula">
            {i.valorUnitario === null ? '-' : MOEDA.format(i.valorUnitario)}
          </div>
          <div className="dcr-celula">{i.total === null ? '-' : MOEDA.format(i.total)}</div>
        </div>
      ))}
    </ModalDeDetalhe>
  );
}
